import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { queryKeys } from "@/lib/queryKeys";
import { invalidateMaintenanceQueries } from "@/lib/mutationHelpers";
import { deleteGuestGuideKnowledge } from "@/lib/guestGuideHospitable";

export interface Appliance {
  id: string;
  property_id: string;
  name: string;
  location: string | null;
  make: string | null;
  model_number: string | null;
  serial_number: string | null;
  purchase_date: string | null;
  warranty_expiry: string | null;
  notes: string | null;
  photo_urls: string[];
  vendor_id: string | null;
  created_at: string;
  updated_at: string;
  guest_visible?: boolean | null;
  guest_set_qty?: number | null;
  guest_place?: string | null;
  guest_place_photo_url?: string | null;
  guest_copy?: string | null;
  guest_guide_status?: string | null;
  hospitable_knowledge_item_id?: string | null;
}

const emptyPhotoUrls: string[] = [];

export function useAppliances(propertyId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.maintenance.appliances(propertyId ?? ""),
    queryFn: async () => {
      if (!propertyId) return [];
      const { data, error } = await supabase
        .from("appliances")
        .select("*")
        .eq("property_id", propertyId)
        .order("name", { ascending: true });
      if (error) throw error;
      return (data ?? []).map((row) => ({
        ...row,
        photo_urls: Array.isArray(row.photo_urls) ? row.photo_urls : emptyPhotoUrls,
      })) as Appliance[];
    },
    enabled: !!propertyId,
  });
}

export function useCreateAppliance(propertyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<Appliance>) => {
      const { data, error } = await supabase
        .from("appliances")
        .insert({
          property_id: propertyId,
          name: input.name ?? "",
          location: input.location ?? null,
          make: input.make ?? null,
          model_number: input.model_number ?? null,
          serial_number: input.serial_number ?? null,
          purchase_date: input.purchase_date ?? null,
          warranty_expiry: input.warranty_expiry ?? null,
          notes: input.notes ?? null,
          photo_urls: input.photo_urls ?? [],
          vendor_id: input.vendor_id ?? null,
        })
        .select()
        .single();
      if (error) throw error;
      return data as Appliance;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.maintenance.appliances(propertyId) });
      invalidateMaintenanceQueries(queryClient);
    },
  });
}

export function useUpdateAppliance(propertyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...input }: Partial<Appliance> & { id: string }) => {
      const { data, error } = await supabase
        .from("appliances")
        .update({
          name: input.name,
          location: input.location,
          make: input.make,
          model_number: input.model_number,
          serial_number: input.serial_number,
          purchase_date: input.purchase_date,
          warranty_expiry: input.warranty_expiry,
          notes: input.notes,
          photo_urls: input.photo_urls,
          vendor_id: input.vendor_id,
        })
        .eq("id", id)
        .eq("property_id", propertyId)
        .select()
        .single();
      if (error) throw error;
      return data as Appliance;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.maintenance.appliances(propertyId) });
      invalidateMaintenanceQueries(queryClient);
    },
  });
}

export function useDeleteAppliance(propertyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data: existing } = await supabase
        .from("appliances")
        .select("hospitable_knowledge_item_id")
        .eq("id", id)
        .eq("property_id", propertyId)
        .maybeSingle();
      await deleteGuestGuideKnowledge({
        propertyId,
        knowledgeItemId: existing?.hospitable_knowledge_item_id,
      });
      const { error } = await supabase
        .from("appliances")
        .delete()
        .eq("id", id)
        .eq("property_id", propertyId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.maintenance.appliances(propertyId) });
      invalidateMaintenanceQueries(queryClient);
    },
  });
}
